/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useRef, useState } from 'react';
import { notifications } from '@/shared/ui/components/basic/Notifications';

/** Outcome the OLX OAuth callback appends to the settings URL. */
export interface OlxOAuthResult {
  status: string;
  message: string | null;
}

/** Callback reasons with a dedicated explanation; anything else is generic. */
const KNOWN_ERRORS = new Set([
  'expired_state', 'invalid_state', 'missing_state', 'denied', 'missing_code', 'token_exchange_failed',
]);

/**
 * Reads and strips ``olx_status``/``olx_message`` from the current URL.
 *
 * @returns The callback outcome, or null when the page was not opened by it.
 */
export function takeOlxOAuthResult(): OlxOAuthResult | null {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('olx_status');
  if (!status) return null;
  const message = params.get('olx_message');
  params.delete('olx_status');
  params.delete('olx_message');
  const search = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
  return { status, message };
}

/**
 * Shows the result of the OLX connection round-trip once, then refreshes the
 * settings so the block reflects the new connection.
 *
 * The URL is cleaned synchronously on mount (a reload must not repeat the
 * toast); the toast waits for the agency id, like the Facebook flow.
 *
 * @param agencyId - The agency id (may resolve after mount).
 * @param t - Translation function.
 * @param fetchSettings - Reloads the portal settings.
 * @returns True when this page was opened by a successful OLX sign-in.
 */
export function useOlxOAuthResult(
  agencyId: string | undefined,
  t: (key: string) => string,
  fetchSettings: () => Promise<void>,
): boolean {
  const [result] = useState(takeOlxOAuthResult);
  const handled = useRef(false);

  useEffect(() => {
    if (!result || !agencyId || handled.current) return;
    handled.current = true;
    if (result.status === 'success') {
      notifications.show({
        title: t('portal.notifications.olxConectadoTitulo'),
        message: t('portal.notifications.olxConectado'),
        type: 'success',
      });
      void fetchSettings();
      return;
    }
    const reason = result.message && KNOWN_ERRORS.has(result.message) ? result.message : 'generic';
    notifications.show({
      title: t('portal.notifications.erroTituloErro'),
      message: t(`portal.notifications.olxErro.${reason}`),
      type: 'error',
    });
  }, [result, agencyId, t, fetchSettings]);

  return result?.status === 'success';
}
